export const getOrFetchOrCreateAndCache = async (
  cacheRegistryRef,
  cacheName,
  itemName,
  fetcherFunction, // This function should now be capable of fetching ALL items if itemName is null/undefined, or a specific item if itemName is provided.
  creatorFunction,
  payloadBuilderFunction,
  orgId,
  accessToken,
  addProgressMessage,
  excelRowIndex,
  itemTypeName,
  dataRow,
  additionalArgs = {}
) => {
  const cache = cacheRegistryRef.current[cacheName];
  if (!cache) {
    console.warn(`Cache "${cacheName}" does not exist in registry.`);
    addProgressMessage(`Internal Warning: Cache "${cacheName}" not initialized.`, "warning");
    return null;
  }

  // New: Check if the cache for this type has already been fully populated
  // We'll use a special key '_isPopulated' in the Map itself to track this.
  if (!cache.get('_isPopulated')) {
    addProgressMessage(`Initial fetch: Populating cache for ${itemTypeName}s...`, "info");
    try {
      // Call the fetcher function to get ALL items of this type
      // The fetcherFunction needs to be designed to return an array of ALL items when itemName is not used for filtering.
      const allFetchedItems = await fetcherFunction(null, orgId, accessToken, addProgressMessage, additionalArgs); // Pass null as itemName to signal "fetch all"

      if (Array.isArray(allFetchedItems) && allFetchedItems.length > 0) {
        allFetchedItems.forEach(item => {
          // Ensure items have a 'name' and 'id' property to be cached
          // If your API returns items with different key names for ID/Name, adjust here.
          if (item.name && item.id) { 
            cache.set(item.name, item); // Cache the full item object by its name
          } else {
            console.warn(`Fetched item for ${itemTypeName} missing 'name' or 'id' property. Item:`, item);
          }
        });
        addProgressMessage(`Successfully populated cache for ${itemTypeName}s with ${allFetchedItems.length} entries.`, "success");
      } else {
        addProgressMessage(`No ${itemTypeName}s found to populate cache.`, "info");
      }
      cache.set('_isPopulated', true); // Mark this cache as populated
    } catch (error) {
      addProgressMessage(`Error during initial cache population for ${itemTypeName}s: ${error.message || String(error)} (Excel Row: ${excelRowIndex})`, "error");
      // If initial population fails, we might still proceed to try fetching individual or creating
      // but it's important to log this failure.
    }
  }

  let itemId = null;

  // 1. Check cache (now potentially populated from initial fetch)
  const cachedItem = cache.get(itemName);
  if (cachedItem && cachedItem.id) {
    addProgressMessage(`${itemTypeName} "${itemName}" found in cache with ID: ${cachedItem.id} (Excel Row: ${excelRowIndex})`, "info");
    return cachedItem.id;
  }

  // 2. If not in cache (and initial population didn't find it), try to fetch from API (individual fetch, if fetcherFunction supports it)
  // This step might be redundant if the initial fetch was comprehensive and successful.
  // We'll keep it for robustness, assuming fetcherFunction can also fetch individual items if needed.
  addProgressMessage(`Attempting individual fetch for ${itemTypeName} "${itemName}" from API (Excel Row: ${excelRowIndex})`, "info");
  try {
    // This call to fetcherFunction should now specifically fetch the 'itemName' if it wasn't found in the bulk fetch.
    // The fetcherFunction needs to handle both 'fetch all' (when itemName is null) and 'fetch by name' scenarios.
    const fetchedItem = await fetcherFunction(itemName, orgId, accessToken, addProgressMessage, additionalArgs);
    if (fetchedItem && fetchedItem.id) {
      cache.set(itemName, fetchedItem); // Cache the full item object
      addProgressMessage(`${itemTypeName} "${itemName}" fetched from API with ID: ${fetchedItem.id} (Excel Row: ${excelRowIndex})`, "info");
      return fetchedItem.id;
    }
  } catch (error) {
    addProgressMessage(`Error during individual fetch for ${itemTypeName} "${itemName}": ${error.message || String(error)} (Excel Row: ${excelRowIndex})`, "error");
    // Do not return null here, allow creation attempt if fetch failed
  }

  // 3. If not found via fetch, try to create
  addProgressMessage(`${itemTypeName} "${itemName}" not found. Initiating creation... (Excel Row: ${excelRowIndex})`, "info");
  try {
    const payload = payloadBuilderFunction(itemName, dataRow, additionalArgs);
    itemId = await creatorFunction(payload, orgId, accessToken, addProgressMessage);

    if (itemId) {
      // Store a simple object with id, as we might not have the full object from creatorFunction
      // If creatorFunction returns the full object with name and id, cache that directly.
      cache.set(itemName, { id: itemId, name: itemName });
      addProgressMessage(`Successfully created ${itemTypeName} "${itemName}" with ID: ${itemId}. (Excel Row: ${excelRowIndex})`, "success");
      return itemId;
    } else {
      addProgressMessage(`Creation of ${itemTypeName} "${itemName}" failed or did not return an ID. (Excel Row: ${excelRowIndex})`, "error");
      return null;
    }
  } catch (error) {
    addProgressMessage(`Error creating ${itemTypeName} "${itemName}": ${error.message || String(error)} (Excel Row: ${excelRowIndex})`, "error");
    return null;
  }
};

// Re-export existing generic cache functions for consistency
export const addToCache = (cacheRegistryRef, cacheName, key, valueToStore) => {
    if (!cacheRegistryRef.current[cacheName]) {
        console.warn(`Cache "${cacheName}" does not exist in registry. Cannot add item.`);
        return;
    }
    cacheRegistryRef.current[cacheName].set(key, valueToStore);
};

export const getFromCache = (cacheRegistryRef, cacheName, key) => {
    if (!cacheRegistryRef.current[cacheName]) {
        console.warn(`Cache "${cacheName}" does not exist in registry. Cannot retrieve item.`);
        return undefined;
    }
    return cacheRegistryRef.current[cacheName].get(key);
};
